import React, { useState, useRef } from 'react';
import ImageCapture from './components/ImageCapture';
import Questionnaire from './components/Questionnaire';
import ResultsPage from './components/ResultsPage';
import { questions } from './data/questions';
import { determineFaceShape, getFaceShapeDescription } from './utils/faceShapeCalculator';

function App() {
  const [step, setStep] = useState('image'); // 'image', 'questionnaire', or 'results'
  const [capturedImage, setCapturedImage] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [faceShape, setFaceShape] = useState('');
  const [faceRatio, setFaceRatio] = useState(0);
  const [chinPosition, setChinPosition] = useState(70);
  const [widowsPeakPosition, setWidowsPeakPosition] = useState(30);
  const [leftFacePosition, setLeftFacePosition] = useState(30);
  const [rightFacePosition, setRightFacePosition] = useState(70);
  const [isDraggingChin, setIsDraggingChin] = useState(false);
  const [isDraggingWidowsPeak, setIsDraggingWidowsPeak] = useState(false);
  const [isDraggingLeftFace, setIsDraggingLeftFace] = useState(false);
  const [isDraggingRightFace, setIsDraggingRightFace] = useState(false);
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);

  const proceedToQuestionnaire = () => {
    if (capturedImage) {
      setStep('questionnaire');
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMouseMove = (e, imageContainerRef) => {
    if (!imageContainerRef.current) return;
    
    const rect = imageContainerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const x = e.clientX - rect.left;
    const percentageY = (y / rect.height) * 100;
    const percentageX = (x / rect.width) * 100;
    
    if (isDraggingChin) {
      setChinPosition(Math.max(0, Math.min(100, percentageY)));
    } else if (isDraggingWidowsPeak) {
      setWidowsPeakPosition(Math.max(0, Math.min(100, percentageY)));
    } else if (isDraggingLeftFace) {
      setLeftFacePosition(Math.max(0, Math.min(100, percentageX)));
    } else if (isDraggingRightFace) {
      setRightFacePosition(Math.max(0, Math.min(100, percentageX)));
    }
  };

  const handleTouchMove = (e, imageContainerRef) => {
    if (!imageContainerRef.current) return;
    
    const touch = e.touches[0];
    const rect = imageContainerRef.current.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const x = touch.clientX - rect.left;
    const percentageY = (y / rect.height) * 100;
    const percentageX = (x / rect.width) * 100;
    
    if (isDraggingChin) {
      setChinPosition(Math.max(0, Math.min(100, percentageY)));
    } else if (isDraggingWidowsPeak) {
      setWidowsPeakPosition(Math.max(0, Math.min(100, percentageY)));
    } else if (isDraggingLeftFace) {
      setLeftFacePosition(Math.max(0, Math.min(100, percentageX)));
    } else if (isDraggingRightFace) {
      setRightFacePosition(Math.max(0, Math.min(100, percentageX)));
    }
  };

  const stopDragging = () => {
    setIsDraggingChin(false);
    setIsDraggingWidowsPeak(false);
    setIsDraggingLeftFace(false);
    setIsDraggingRightFace(false);
  };

  const nextQuestion = () => {
    if (currentQuestion === 0) {
      // Get pixel distance for height
      const containerElement = imageContainerRef.current;
      if (containerElement) {
        const containerRect = containerElement.getBoundingClientRect();
        const chinPixels = (chinPosition / 100) * containerRect.height;
        const widowsPeakPixels = (widowsPeakPosition / 100) * containerRect.height;
        const faceHeightPixels = Math.abs(chinPixels - widowsPeakPixels);
        
        setAnswers(prev => ({ 
          ...prev, 
          0: { 
            chin: chinPosition, 
            widowsPeak: widowsPeakPosition,
            faceHeight: faceHeightPixels
          } 
        }));
      }
    } else if (currentQuestion === 1) {
      // Get pixel distance for width
      const containerElement = imageContainerRef.current;
      if (containerElement) {
        const containerRect = containerElement.getBoundingClientRect();
        const leftPixels = (leftFacePosition / 100) * containerRect.width;
        const rightPixels = (rightFacePosition / 100) * containerRect.width;
        const faceWidthPixels = Math.abs(rightPixels - leftPixels);
        const faceHeightPixels = answers[0]?.faceHeight || 0;
        const faceRatio = faceWidthPixels > 0 ? faceHeightPixels / faceWidthPixels : 0;
        
        setAnswers(prev => ({ 
          ...prev, 
          1: { 
            leftFace: leftFacePosition, 
            rightFace: rightFacePosition,
            faceWidth: faceWidthPixels,
            faceRatio: faceRatio
          } 
        }));
      }
    }
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    // Determine face shape based on answers
    const determinedShape = determineFaceShape(answers);
    const description = getFaceShapeDescription(determinedShape);
    const ratio = answers[1]?.faceRatio || 0;
    
    console.log('Submitted answers:', answers);
    console.log('Image:', capturedImage);
    console.log('Measurements:', {
      faceHeight: answers[0]?.faceHeight,
      faceWidth: answers[1]?.faceWidth,
      faceRatio: ratio
    });
    console.log('Determined Face Shape:', determinedShape);
    console.log('Description:', description);
    
    // Set results and navigate to results page
    setFaceShape(determinedShape);
    setFaceRatio(ratio);
    setStep('results');
  };

  const resetApp = () => {
    setCapturedImage(null);
    setAnswers({});
    setStep('image');
    setCurrentQuestion(0);
    setFaceShape('');
    setFaceRatio(0);
    setChinPosition(70);
    setWidowsPeakPosition(30);
    setLeftFacePosition(30);
    setRightFacePosition(70);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {step === 'image' && (
          <ImageCapture
            capturedImage={capturedImage}
            setCapturedImage={setCapturedImage}
            onProceed={proceedToQuestionnaire}
          />
        )}
        
        {step === 'questionnaire' && (
          <Questionnaire
            capturedImage={capturedImage}
            questions={questions}
            currentQuestion={currentQuestion}
            answers={answers}
            chinPosition={chinPosition}
            widowsPeakPosition={widowsPeakPosition}
            leftFacePosition={leftFacePosition}
            rightFacePosition={rightFacePosition}
            isDraggingChin={isDraggingChin}
            isDraggingWidowsPeak={isDraggingWidowsPeak}
            isDraggingLeftFace={isDraggingLeftFace}
            isDraggingRightFace={isDraggingRightFace}
            setIsDraggingChin={setIsDraggingChin}
            setIsDraggingWidowsPeak={setIsDraggingWidowsPeak}
            setIsDraggingLeftFace={setIsDraggingLeftFace}
            setIsDraggingRightFace={setIsDraggingRightFace}
            handleMouseMove={handleMouseMove}
            handleTouchMove={handleTouchMove}
            stopDragging={stopDragging}
            handleAnswerChange={handleAnswerChange}
            prevQuestion={prevQuestion}
            nextQuestion={nextQuestion}
            handleSubmit={handleSubmit}
            imageRef={imageRef}
            imageContainerRef={imageContainerRef}
          />
        )}

        {step === 'results' && (
          <ResultsPage
            capturedImage={capturedImage}
            faceShape={faceShape}
            faceRatio={faceRatio}
            answers={answers}
            onReset={resetApp}
          />
        )}
      </div>
    </div>
  );
}

export default App;
